import { PosType } from './api/location.types';
import { ModifierType } from './api/menu.types';
import {
  DeliveryAssignmentEntity,
  FundedTipScheduleEntity,
  SimpleLocationEntity,
  VDriveProviderInfo,
} from './location.types';
import { Provider } from './shared.types';
import { VDriveStatus } from './shared.types';
import { SimpleUserEntity } from './user.types';

export enum BrandType {
  vGrubs = 'vGrubs',
  TDOC = 'TDOC',
  RB = 'RB',
  VDC = 'VDC',
  VPC = 'VPC',
  TLB = 'TLB',
  FJ = 'FJ',
}

export interface SipDetails {
  server: string;
  user: string;
  password: string;
  phoneNumber: string;
}
export interface Brand {
  id: number;
  storeId: number;
  name: string;
  locationId: number;
  isLocation: boolean;
  crmApiKey: string | null;
  providers: number[];
  imageUrl: string | null;
  notes: string | null;
  sip: SipDetails | null;
  type: BrandType;
  vDriveStatus: VDriveStatus | null;
  timeZone: string;
  paused: boolean;
  exclusiveOfferEnabled: boolean;
  isActivating: boolean;
  vOrdersOrderingUrl?: string | null;
  /** Review link after a 4-5 star rating; used only for a virtual brand. */
  googleBusinessUrl?: string | null;
  pushOrdersToPos?: boolean;
}

export type SimpleBrandEntity = Pick<Brand, 'id' | 'name' | 'isLocation'> & {
  isVGrubsBrand: boolean;
};

export type TrackingBrandEntity = Pick<
  Brand,
  'id' | 'name' | 'imageUrl' | 'type' | 'isLocation'
> & {
  isVGrubsBrand: boolean;
  isPhysicalLocation: boolean;
  isActivating: boolean;
};

export type SimpleBrandWithImageEntity = Pick<
  Brand,
  'id' | 'name' | 'isLocation' | 'imageUrl' | 'type' | 'pushOrdersToPos'
> & {
  isVGrubsBrand: boolean;
  isActivating: boolean;
  isPhysicalLocation: boolean;
};

export type ActiveBrandEntity = Pick<Brand, 'id' | 'name' | 'imageUrl'> & {
  totalGain: number;
  isActivating: boolean;
};

/** A location's own brand (RB and the other non-vGrubs types), from
 * `/v2/locations/:id/brands/own`. The merchant manages its menu and hours. */
export type OwnBrandEntity = Pick<Brand, 'id' | 'name' | 'imageUrl' | 'type'>;

export type BrandProviderConfigEntity = {
  provider: Provider;
  username?: string | null;
  password?: string | null;
  storeId?: string | null;
};

export type BrandVDriveEntity = {
  createdAt: string;
  brand: SimpleBrandEntity;
  merchantId: string | null;
  autoAssign: boolean;
  applyFromPhysicalLocation: boolean;
  providers: Array<VDriveProviderInfo>;
  fundedTipSchedule: Array<FundedTipScheduleEntity>;
  fundingTipOrigin: boolean;
  deliveryAssignment: DeliveryAssignmentEntity | null;
  locationStatus: VDriveStatus;
  cwTeamId?: string | null;
  /**
   * The vDrive fleet this is pinned to ("VD-10012"): its quote is asked first
   * and always wins. `null` when not pinned.
   */
  vdriveOrganizationId?: string | null;
  /** The pinned fleet's name as it was when saved. */
  vdriveOrganizationName?: string | null;
  exclusiveOfferEnabled: boolean;
  callDispatcherEnabled: boolean;
  callDispatcherMinutes?: number | null;
  motoClickEnabled?: boolean | null;
  /**
   * Pickup note handed to the delivery company and shown to the driver.
   * `null` when none is set. Max 280 chars - longer is a 400 from the backend.
   */
  pickupInstructions?: string | null;
};

export type BrandTemplateEntity = {
  id: number;
  name: string;
  phoneNumber?: string | null;
  imageUrl: string;
  printingImageUrl?: string | null;
  printingMessage?: string | null;
  heroImageUrl?: string | null;
  menuPdfExampleUrl?: string | null;
  mostPopular: boolean;
  visible: boolean;
  foodTypes: FoodType[];
  isMenuSet: boolean;
};

export enum FoodType {
  all = 'all',
  breakfast = 'breakfast',
  lunch = 'lunch',
  dinner = 'dinner',
}

export enum BrandPausedTimeStatus {
  active = 'active',
  expired = 'expired',
  upcoming = 'upcoming',
}

export type BrandPausedTimeEntity = {
  id: number;
  status: BrandPausedTimeStatus;
  startDate: string;
  endDate: string;
  providers: Provider[];
};

export type BrandPausedTimeDto = {
  providers: Provider[];
  startDate: string;
  endDate: string;
  id?: number | null;
};

export interface BrandRequestEntity {
  id: number;
  name: string;
  location: SimpleLocationEntity;
  owner: SimpleUserEntity;
  imageUrl: string | null;
  createdAt: string;
}

export interface BrandRequestDetailsEntity {
  id: number;
  name: string;
  location: SimpleLocationEntity;
  owner: SimpleUserEntity;
  imageUrl: string | null;
  createdAt: string;
  activating: boolean;
  brands: Array<ActiveBrandEntity>;
  menu?: BrandMenuEntity | null;
  posType?: PosType | null;
}

export type BrandMenuEntity = {
  categories: Array<BTCategoryResponse>;
  modifierGroups: Array<BTModifierGroupItemResponse>;
};

export type BTCategoryResponse = {
  id: number;
  name: string;
  description?: string | null;
  items: Array<BTMenuItemResponse>;
  minItems?: number | null;
  maxItems?: number | null;
};

export type BTMenuItemResponse = {
  id: number;
  name: string;
  description?: string | null;
  chosenPrice?: number | null;
  fromPrice: number;
  toPrice: number;
  preparationNotes?: string | null;
  imageUrl?: string | null;
  modifierGroups: Array<BTModifierGroupItemResponse>;
};

export type BTModifierGroupItemResponse = {
  id: number;
  name: string;
  type: ModifierType;
  description?: string | null;
  required: boolean;
  selectionsMin: number;
  selectionsMax: number;
  options: Array<BTModifierItemResponse>;
};

export type BTModifierItemResponse = {
  id: number;
  name: string;
  description?: string | null;
  chosenPrice?: number | null;
  fromPrice: number;
  toPrice: number;
};

export type BTUpdateItemRequestBody = {
  id: number;
  price: number;
  modifierGroups: Array<BTUpdateItemModifierGroupRequestBody>;
};

type BTUpdateItemModifierGroupRequestBody = {
  id: number;
  options: Array<BTUpdateItemModifierGroupOptionRequestBody>;
};

type BTUpdateItemModifierGroupOptionRequestBody = {
  id: number;
  price: number;
};

export type BTMenuWithTemplateResponse = {
  menu: BTMenuResponse;
  template: BrandTemplateEntity;
};

export type BTMenuResponse = {
  categories: Array<BTCategoryResponse>;
  modifierGroups: Array<BTModifierGroupItemResponse>;
};

export type UpdateBTMenuModifierGroupDto = {
  name?: string;
  description?: string;
  type?: ModifierType;
  required?: boolean;
  selectionsMin?: number;
  selectionsMax?: number;
  options: UpdateBTMenuOptionDto[];
};

export type UpdateBTMenuOptionDto = {
  id: number;
  name: string;
  description?: string;
  fromPrice: number;
  toPrice: number;
};

export type CreateBTMenuModifierDto = {
  name: string;
  description: string;
  type: ModifierType;
  options: CreateBTMenuOptionDto[];
  required: boolean;
  selectionsMin: number;
  selectionsMax: number;
};

export type CreateBTMenuOptionDto = {
  name: string;
  description?: string;
  fromPrice: number;
  toPrice: number;
};

export type CreateBTMenuCategoryDto = {
  name: string;
  description: string;
  minItems?: number | null;
  maxItems?: number | null;
};

export type UpdateBTMenuCategoryDto = {
  name: string;
  description: string;
  minItems?: number | null;
  maxItems?: number | null;
};

export type BTImportMenuUrlPreviewResponse = {
  started: boolean;
  finished: boolean;
  menu?: BTMenuResponse | null;
};

export type InitiateBrandRequestDto = {
  brandTemplateId: number;
  locationId: number;
  items: Array<InitiateBrandRequestItemWithImageUrlDto>;
};

export type InitiateBrandRequestItemWithImageUrlDto = {
  id: number;
  price: number;
  imageUrl?: string | null;
  modifierGroups: Array<InitiateBrandRequestItemModifierGroupDto>;
};

export type InitiateBrandRequestItemModifierGroupDto = {
  id: number;
  options: Array<InitiateBrandRequestItemOptionDto>;
};

export type InitiateBrandRequestItemOptionDto = {
  id: number;
  price: number;
};

export type UpdateBTMenuListOrdersDto = {
  id: number;
  order: number;
};
